//* Libraries imports
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

//* Components imports
import { ProcessEssayList } from "./process-essay-list";
import { ErrorMessages } from "../../../../../../component/common/ErrorMessages";
import { EssayTemplateModal } from "./essay-template-modal";

//* Hooks imports
import { useGetPaginatedProcessExams } from "../../../../../../hooks/queries/essay/use-get-paginted-essay";

//* Types imports
import { type PaginatedEssay } from "../../../../../../schemas/common/essay/essay";

function transformEssayData(items: PaginatedEssay[]) {
  return items.map((item) => ({
    id: item.id,
    name: item.title, 
    title: item.title,
    statement: item.statement,
    correctionStatus: item.correctionStatus,
    textoRedacaoId: item.textoRedacaoId,
  }));
}

export function ProcessEssayTable({ processoId }: { processoId: number }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const page = parseInt(searchParams.get("page") || "1", 10);
  const perPage = 10;
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedEssayId, setSelectedEssayId] = useState<number | null>(null);
  const [selectedEssayTitle, setSelectedEssayTitle] = useState<string>("");

  const Results = useGetPaginatedProcessExams(
    processoId,
    page,
    perPage,
    searchTerm
  );

  useEffect(() => {
    if (Results.isError && Results.error) {
      const error = Results.error as any;
      const message =
        typeof error === "string"
          ? error
          : error?.message ||
            "Ocorreu um erro ao buscar as redações.";
      setErrorMessages([message]);
    }
  }, [Results.isError, Results.error]);

  const clearErrorMessages = () => {
    setErrorMessages([]);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearch = () => {
    // Only search if term has 3+ characters or is empty (to show all)
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (searchTerm) {
        newSearchParams.set("search", searchTerm);
      } else {
        newSearchParams.delete("search");
      }
      newSearchParams.set("page", "1");
      setSearchParams(newSearchParams);
    }
  };

  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };
  
  const handleViewEssayResults = (textoRedacaoId: number) => {
    navigate(`results/${textoRedacaoId}`);
  };
  
  const handleViewEssay = (id: number) => {
    const essay = transformedItems.find(item => item.id === id);
    if (essay) {
      setSelectedEssayId(id);
      setSelectedEssayTitle(essay.title);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEssayId(null);
    setSelectedEssayTitle("");
  };

  const transformedItems = Results.data?.data 
    ? transformEssayData(Results.data.data)
    : [];

  const totalItems = Results.data?.meta?.totalItems || Results.data?.data?.length || 0;
  const totalPages = Math.ceil(totalItems / perPage);
    const fields = [
    { name: 'name', totalQuestions: 'name', header: 'Nome da Redação', type: 'text' as const },
    { name: 'title', totalQuestions: 'title', header: 'Título', type: 'text' as const },
  ];
  return (
    <div className="w-100 p-2">
      <ErrorMessages 
        messages={errorMessages} 
        clearErrors={clearErrorMessages} 
      />

      <ProcessEssayList
        title="Redações do Processo"
        searchPlaceholder="Pesquisar redações..."
        notFoundMessage="Nenhuma redação encontrada"
        fields={fields}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onSearch={handleSearch}
        onViewProcessExam={handleViewEssay}
        onViewExamResults={handleViewEssayResults}
        items={transformedItems}
        loading={Results.isLoading}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={perPage}
        onPageChange={handlePageChange}
      />

      {/* Essay Template Modal */}
      {showModal && selectedEssayId && (
        <EssayTemplateModal
          show={showModal}
          onHide={handleCloseModal}
          processoId={processoId}
          essayId={selectedEssayId}
          essayTitle={selectedEssayTitle}
        />
      )}
    </div>
  );
}
